import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AreaApi from "../../services/AreaApi";
import PlantApi from "../../services/PlantApi";
import ProductionUnitApi from "../../services/ProductionUnitApi";
import { useToast } from "../../context/ToastContext";

// Design System
import FormCard from "../../components/FormCard";
import Input from "../../components/Input/Input";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";

const CreateAreaForm: React.FC = () => {
  const navigate = useNavigate();
  const { displayToast } = useToast();
  const { t: translateForm } = useTranslation("form");
  const { t: translateMessages } = useTranslation("messages");
  const { t: translateGeneral } = useTranslation("general");

  const [name, setName] = useState("");
  const [plantId, setPlantId] = useState("");
  const [productionUnitId, setProductionUnitId] = useState("");
  
  const [plants, setPlants] = useState<{ label: string; value: string }[]>([]);
  const [productionUnits, setProductionUnits] = useState<{ label: string; value: string }[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  useEffect(() => {
    fetchPlants();
  }, []);

  useEffect(() => {
    if (plantId) {
      fetchProductionUnits(plantId);
    } else {
      setProductionUnits([]);
      setProductionUnitId("");
    }
  }, [plantId]);

  const fetchPlants = async (): Promise<void> => {
    try {
      const response = await PlantApi.GetAllPlants();
      const options = (response.data || []).map((p: any) => ({
        label: p.name,
        value: p.id,
      }));
      setPlants(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar plantas: ", error);
    }
  };

  const fetchProductionUnits = async (pId: string): Promise<void> => {
    try {
      const response = await ProductionUnitApi.GetProductUnitByPlant(pId);
      const options = (response.data || []).map((u: any) => ({
        label: u.name,
        value: u.id,
      }));
      setProductionUnits(options.sort((a: any, b: any) => a.label.localeCompare(b.label)));
    } catch (error) {
      console.error("Erro ao buscar unidades de produção: ", error);
    }
  };

  const handleSave = async (): Promise<void> => {
    if (!name || !productionUnitId) {
      displayToast("error", translateForm("error"), translateMessages("fillAllFields"), false, 3000);
      return;
    }

    setIsLoading(true);
    try {
      // Duplicity check
      const areasResponse = await AreaApi.GetAllAreas();
      const areas = areasResponse.data || [];
      const exists = areas.some((a: any) => a.name.toLowerCase() === name.toLowerCase() && a.productionUnitId === productionUnitId);

      if (exists) {
        displayToast("error", translateMessages("duplicateAreaError"), "", false, 3000);
        return;
      }

      await AreaApi.CreateArea(productionUnitId, name);
      displayToast("success", translateMessages("registerSuccess"), "", false, 3000);
      navigate("/areas");
    } catch (error: any) {
      displayToast("error", translateForm("error"), error.message || error, false, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ConfirmModal
        visible={showConfirmCancel}
        onHide={() => setShowConfirmCancel(false)}
        onConfirm={() => navigate("/areas")}
      />
      <FormCard
        title={translateMessages("newArea")}
        icon="add"
        onSubmit={handleSave}
        onCancel={() => setShowConfirmCancel(true)}
        submitButtonLabel={translateGeneral("save")}
        cancelButtonLabel={translateGeneral("cancel")}
        disabled={isLoading}
        numColumns={6}
      >
      <div style={{ gridColumn: "span 6" }}>
        <Input
          type="text"
          label={translateForm("name")}
          required
          value={name}
          onChange={(val: any) => setName(val)}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("plant")}
          required
          value={plantId}
          options={plants}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => {
            setPlantId(val);
            setProductionUnitId("");
          }}
        />
      </div>
      <div style={{ gridColumn: "span 3" }}>
        <Input
          type="select"
          label={translateForm("productionUnit")}
          required
          disabled={!plantId}
          value={productionUnitId}
          options={productionUnits}
          optionLabel="label"
          optionValue="value"
          onChange={(val: any) => setProductionUnitId(val)}
        />
      </div>
    </FormCard>
    </>
  );
};

export default CreateAreaForm;
